import StoryblokClient from "storyblok-js-client";
import { renderRichText } from "@storyblok/richtext";
import { makeAutoObservable, runInAction } from "mobx";
import { get } from 'lodash';

// Component identifiers to fetch content by storyblok slugs.
const ENTRIES = {
  footer: 'footer',
  home: 'homepage',
  homeInsurance: 'home-insurance',
  homeQuickQuote: 'home-insurance/quick-quote',
  lifeInsurance: 'life-insurance',
  lifeInsuranceCoverages: 'life-insurance/types-of-coverages',
  motorQuickQuote: 'motor-insurance/quick-quote',
  privacyPolicy: 'legal/privacy-policy',
  termsOfUse: 'legal/terms-of-use',
  groupHealth: 'business-insurance/group-health',
  groupLife: 'business-insurance/group-life',
  smallBusiness: 'business-insurance/small-business',
  eventsLiability: 'business-insurance/event-liability',
  aboutUs: 'about-us',
  claimsSupport: 'customer-care/claims-support',
  contactUs: 'customer-care/contact-us',
  paymentOptions: 'customer-care/payment-options',
  renewals: 'customer-care/renewals',
  insurerContacts: 'customer-care/insurer-contacts',
  faq: 'customer-care/faq',
};

class ContentStore {
  storyBlockClient = new StoryblokClient({
    accessToken: process.env.REACT_APP_STORYBLOK_TOKEN,
    apiVersion: 'v2'
  });

  IS_LOADING = -1;
  IS_FETCH_ERROR = -2;
  IS_NOT_LOADED = -3;

  // Fetched page content store, keyed by Storyblok slug.
  content = Object.entries(ENTRIES).reduce((obj, curr) =>
    ({ ...obj, [curr[1]]: this.IS_NOT_LOADED, }), {});

  currentPage; // Define which page is currently being shown.

  constructor() {
    makeAutoObservable(this, {}, { autoBind: true });
  }

  getBlocks(componentName) {
    if (this.content[componentName] === undefined) {
      return (key, defaultValue = "Nothing loaded.") => defaultValue;
    }
    return (key, defaultValue) => get(this.content[componentName], key, defaultValue);
  }

  fetchBlock = async blockName => {
    if (this.content[blockName] === this.IS_NOT_LOADED) {
      this.__fetchBlock(blockName);
    }
  }

  fetchGlobalBlocks = async () => {
    if (this.content.footer === this.IS_NOT_LOADED) {
      this.__fetchBlock('footer');
    }
  }

  __fetchBlock = async blockName => {
    runInAction(() => {
      this.content[blockName] = this.IS_LOADING;
    });
    const version = process.env.REACT_APP_STORYBLOK_VERSION || "published";

    try {
      const res = await this.storyBlockClient.get(`cdn/stories/${blockName}`, { version });
      runInAction(() => {
        this.content[blockName] = res.data.story.content;
      });
    } catch (err) {
      runInAction(() => {
        this.content[blockName] = this.IS_FETCH_ERROR;
      });
    }
  };

  extractPlainText(component) {
    if (!component) {
      return "";
    }

    if (typeof component === "string") {
      return component;
    }

    if (Array.isArray(component)) {
      return component.map((node) => this.extractPlainText(node)).join("");
    }

    const text = component.text || "";
    const children = Array.isArray(component.content)
      ? component.content.map((node) => this.extractPlainText(node)).join("")
      : "";

    return `${text}${children}`;
  }

  renderComponent(component) {
    if (!component) {
      return "";
    }

    if (typeof component === "string") {
      return component;
    }

    const resolver = this.storyBlockClient?.richTextResolver;

    if (resolver && typeof resolver.render === "function") {
      return resolver.render(component);
    }

    try {
      return renderRichText(component);
    } catch (err) {
      console.warn("Storyblok rich text rendering failed:", err);
      return this.extractPlainText(component);
    }
  }
}

export { ENTRIES };

export default ContentStore
